import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    hideCategories = "hideCategories",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
        [fields.hideCategories]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    shopAll?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
}

export const categoryListStyles: CategoryListStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
        offsetCss: css`
            justify-content: center;
        `,
    },
    categoryItem: {
        width: 0,
        css: css`
            transition: background-color 0.8s;
            display: flex;
            justify-content: center;
            align-items: center;
            &:empty {
                display: none;
            }

            &:hover {
                background-color: #eff2f5;
                .shopClass {
                    color: ${getColor("primary")};
                }
            }
        `,
    },
    innerContainer: {
        gap: 10,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 160px;
            display: grid;
            justify-content: center;
            align-content: center;
            margin: 23px 0 !important;
        `,
    },
    categoryImage: {
        css: css`
            width: 180px;
            height: 180px;
            box-shadow: 0px 0px 14px rgba(0, 0, 0, 0.07);
            border-radius: 50%;
            background: #fff;
            img {
                max-height: 126px;
                max-width: 126px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: 190px;
                            height: 190px;
                            img {
                                max-height: 139px;
                                max-width: 139px;
                            }
                        `,
                    ],
                    "max",
                )}
        `,
    },

    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding-bottom: 8px;
            margin: 6px 0;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },

    categoryNameLink: {
        css: css`
            display: grid;
            justify-content: center;
        `,
        typographyProps: {
            size: "1.25rem",
            fontFamily: "Roboto-Bold",
            css: css`
                color: ${getColor("secondary")} !important;
                text-align: center;
                height: 2.875rem;
                line-height: initial;
            `,
        },
    },
    shopAll: {
        css: css`
            color: transparent;
            font-size: 14px;
            text-align: center;
            transition: color 0.5s;
            display: block;
        `,
    },

    subCategoryNameLinkItem: {
        width: 12,
    },
};

const styles = categoryListStyles;

class SEHomePageCategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    categoryItemStyles: GridItemProps = { ...styles.categoryItem };

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    recalculateWidth = () => {
        if (!this.container.current) {
            return;
        }

        const { breakpoints } = this.props.theme;
        const { clientWidth } = this.container.current;
        let width: GridWidths;
        if (clientWidth < breakpoints.values[1]) {
            width = 12;
        } else if (clientWidth < breakpoints.values[2]) {
            width = 6;
        } else if (clientWidth < breakpoints.values[3]) {
            width = 4;
        } else {
            width = 2;
        }

        if (width === this.categoryItemStyles.width) {
            return;
        }

        this.categoryItemStyles.width = width;
        this.forceUpdate();
    };

    componentDidMount() {
        this.recalculateWidth();
        window.addEventListener("resize", this.recalculateWidth, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.recalculateWidth);
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.categoriesDataView.value && this.props.categoriesDataView.value) {
            this.recalculateWidth();
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            fields: { showImages, showOnlyTopLevelCategories, hideCategories },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        return (
            <StyledWrapper ref={this.container}>
                <GridContainer {...styles.container}>
                    {categories.map(category => (
                        <>
                            {category.subCategoryIds &&
                                category.subCategoryIds.map(subCategoryId => (
                                    <GridItem key={subCategoryId} {...this.categoryItemStyles}>
                                        <SubCategoryLink
                                            categoryId={subCategoryId}
                                            showImages={showImages}
                                            hideCategories={hideCategories}
                                        />
                                    </GridItem>
                                ))}
                        </>
                    ))}
                </GridContainer>
            </StyledWrapper>
        );
    }
}

type SubCategoryLinkViewProps = { showImages: boolean; hideCategories: string } & ReturnType<
    typeof linkMapStateToProps
>;

const SubCategoryLinkView = ({ category, showImages, hideCategories }: SubCategoryLinkViewProps) => {
    if (!category) {
        return null;
    }
    const categoryName = hideCategories;
    const categoryArr = categoryName?.split(",");

    const hideCatergory = !categoryArr?.includes(category.shortDescription);

    return hideCatergory ? (
        <GridContainer {...styles.innerContainer}>
            {showImages && (
                <GridItem {...styles.categoryImageItem}>
                    {category.path && (
                        <Link href={category.path} {...styles.categoryImageLink}>
                            <LazyImage
                                src={category.smallImagePath}
                                {...styles.categoryImage}
                                altText={category.imageAltText}
                            />
                        </Link>
                    )}
                </GridItem>
            )}
            <GridItem {...styles.categoryNameLinkItem}>
                <Link href={category.path} {...styles.categoryNameLink}>
                    {category.shortDescription}
                </Link>
            </GridItem>
            <GridItem {...styles.categoryNameLinkItem}>
                <Link
                    href={category.path}
                    {...styles.categoryNameLink}
                    aria-label={`Shop all ${category.shortDescription} products`}
                >
                    <Typography className="shopClass" {...styles.shopAll}>
                        {translate("Shop All")}
                    </Typography>
                </Link>
            </GridItem>
        </GridContainer>
    ) : null;
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(SEHomePageCategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.hideCategories,
                displayName: "Hide Category",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
